/* eslint-disable camelcase */
/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * This file exports all controls available for use in chart plugins internal to Superset.
 * It is not recommended to use the controls here for any third-party plugins.
 *
 * While the React components located in `controls/components` represent different
 * types of controls (CheckboxControl, SelectControl, TextControl, ...), the controls here
 * represent instances of control types, that can be reused across visualization types.
 *
 * When controls are reused across viz types, their values are carried over as a user
 * changes the chart types.
 *
 * While the keys defined in the control itself get passed to the controlType as props,
 * here's a list of the keys that are common to all controls, and as a result define the
 * control interface.
 */
import { isEmpty, isEqual } from 'lodash';
import {
  t,
  getCategoricalSchemeRegistry,
  getSequentialSchemeRegistry,
  SequentialScheme,
  legacyValidateInteger,
  ComparisonType,
  ensureIsArray,
  isDefined,
  validateMaxValue,
  timeRangeValidForTimeGran,
  timeGranValidForTimeRange,
  validateTimeRangeType,
  SampleMode,
  JsonArray,
  QueryFormMetric,
  FeatureFlag,
  isFeatureEnabled,
} from '@superset-ui/core';

import {
  formatSelectOptions,
  displayTimeRelatedControls,
  D3_FORMAT_OPTIONS,
  D3_FORMAT_DOCS,
  D3_TIME_FORMAT_OPTIONS,
  D3_TIME_FORMAT_DOCS,
  DEFAULT_TIME_FORMAT,
  DEFAULT_NUMBER_FORMAT,
} from '../utils';
import {
  DEFAULT_MAX_ROW,
  TIME_FILTER_LABELS,
  SampleModeLabel,
} from '../constants';
import {
  SharedControlConfig,
  Dataset,
  ControlState,
  ControlPanelState,
  ControlStateMapping,
} from '../types';

import {
  dndAdhocFilterControl,
  dndAdhocMetricControl,
  dndAdhocMetricsControl,
  dndGranularitySqlaControl,
  dndSortByControl,
  dndSecondaryMetricControl,
  dndSizeControl,
  dndXControl,
  dndYControl,
  dndColumnsControl,
  dndEntityControl,
  dndGroupByControl,
  dndSeriesControl,
  dndAdhocMetricControl2,
  dndXAxisControl,
  measurements,
} from './dndControls';

const categoricalSchemeRegistry = getCategoricalSchemeRegistry();
const sequentialSchemeRegistry = getSequentialSchemeRegistry();

export const PRIMARY_COLOR = { r: 0, g: 122, b: 135, a: 1 };

const ROW_LIMIT_OPTIONS = [10, 50, 100, 250, 500, 1000, 5000, 10000, 50000];
const SERIES_LIMITS = [5, 10, 20];
const SAMPLE_RATE_OPTIONS = [
  [0.0001, '0.01%'],
  [0.001, '0.1%'],
  [0.01, '1%'],
  [0.1, '10%'],
  [0.2, '20%'],
  [0.5, '50%'],
];

const appContainer = document.getElementById('app');
const { user } = JSON.parse(
  appContainer?.getAttribute('data-bootstrap') || '{}',
);

export const isKustoSamplingDatasource = (
  controls: ControlStateMapping,
  featureEnabled = isFeatureEnabled(FeatureFlag.KustoSampling),
) => {
  const datasource = controls?.datasource?.datasource;
  return (
    featureEnabled &&
    datasource?.database?.backend === 'kustokql' &&
    datasource?.is_sqllab_view !== true &&
    datasource?.kusto_sampling_enabled === true
  );
};

export const isKustoUserFlow = (controls: ControlStateMapping) =>
  controls?.datasource?.datasource?.database?.backend === 'kustokql' &&
  controls?.viz_type?.value === 'userFlow';

export const isSampleModeVisible = (controls: ControlStateMapping) => {
  if (isKustoUserFlow(controls)) {
    return false;
  }

  const datasource = controls?.datasource?.datasource;
  const backend = datasource?.database?.backend;
  const supportedBackend =
    backend === 'clickhouse' || isKustoSamplingDatasource(controls);
  return Boolean(
    user?.roles &&
      datasource?.is_sqllab_view !== true &&
      supportedBackend &&
      Object.values(user.roles).some((permissions: any) =>
        permissions.some(
          (permission: any) =>
            permission[0] === 'can_make_sample_query' &&
            permission[1] === 'Superset',
        ),
      ),
  );
};

const isSampleRateVisible = (controls: ControlStateMapping) =>
  controls?.datasource?.datasource?.database?.backend === 'clickhouse' &&
  isSampleModeVisible(controls);

type SelectDefaultOption = {
  label: string;
  value: string;
};

const datasourceControl: SharedControlConfig<'DatasourceControl'> = {
  type: 'DatasourceControl',
  label: t('Datasource'),
  default: null,
  description: null,
  mapStateToProps: ({ datasource, form_data }) => ({
    datasource,
    form_data,
    user,
  }),
};

const viz_type: SharedControlConfig<'VizTypeControl'> = {
  type: 'VizTypeControl',
  label: t('Visualization Type'),
  default: 'table',
  description: t('The type of visualization to display'),
};

const color_picker: SharedControlConfig<'ColorPickerControl'> = {
  type: 'ColorPickerControl',
  label: t('Fixed Color'),
  description: t('Use this to define a static color for all circles'),
  default: PRIMARY_COLOR,
  renderTrigger: true,
};

const linear_color_scheme: SharedControlConfig<'ColorSchemeControl'> = {
  type: 'ColorSchemeControl',
  label: t('Linear Color Scheme'),
  choices: () =>
    (sequentialSchemeRegistry.values() as SequentialScheme[]).map(value => [
      value.id,
      value.label,
    ]),
  default: sequentialSchemeRegistry.getDefaultKey(),
  clearable: false,
  description: '',
  renderTrigger: true,
  schemes: () => sequentialSchemeRegistry.getMap(),
  isLinear: true,
  mapStateToProps: state => ({
    dashboardId: state?.form_data?.dashboardId,
  }),
};

const granularity: SharedControlConfig<'SelectControl'> = {
  type: 'SelectControl',
  freeForm: true,
  label: TIME_FILTER_LABELS.granularity,
  default: 'one day',
  choices: [
    [null, t('all')],
    ['PT5S', t('5 seconds')],
    ['PT30S', t('30 seconds')],
    ['PT1M', t('1 minute')],
    ['PT5M', t('5 minutes')],
    ['PT30M', t('30 minutes')],
    ['PT1H', t('1 hour')],
    ['PT6H', t('6 hour')],
    ['P1D', t('1 day')],
    ['P7D', t('7 days')],
    ['P1W', t('week')],
    ['week_starting_sunday', t('week starting Sunday')],
    ['week_ending_saturday', t('week ending Saturday')],
    ['P28D', t('28 days')],
    ['P1M', t('month')],
    ['P3M', t('quarter')],
    ['P1Y', t('year')],
  ],
  description: t(
    'The time granularity for the visualization. Note that you ' +
      'can type and use simple natural language as in `10 seconds`, ' +
      '`1 day` or `56 weeks`',
  ),
};

const time_grain_sqla: SharedControlConfig<'SelectControl'> = {
  type: 'SelectControl',
  label: TIME_FILTER_LABELS.time_grain_sqla,
  placeholder: t('None'),
  initialValue: (control: ControlState, state: ControlPanelState) => {
    if (!isDefined(state)) {
      // If a chart is in a Dashboard, the ControlPanelState is empty.
      return control.value;
    }

    // If a chart is saved, metadata will be defined. But if time_grain_sqla
    // is not defined in the form, this chart is being converted to one that
    // does, in which case we want to default to P1D
    if (state.metadata && !('time_grain_sqla' in (state?.form_data ?? {}))) {
      return 'P1D';
    }

    // If a chart is a new one that isn't saved, metadata is null. In this
    // case we want to default P1D. If the chart has been saved, we want
    // to use whichever value was chosen, either nothing or valid a time grain.
    return state?.metadata || 'time_grain_sqla' in (state?.form_data ?? {})
      ? state?.form_data?.time_grain_sqla
      : 'P1D';
  },
  description: t(
    'Select a time grain for the visualization. The ' +
      'grain is the time interval represented by a ' +
      'single point on the chart.',
  ),
  mapStateToProps: (state, controlState) => {
    if (sessionStorage) {
      const currentValue: string = (
        controlState?.value ||
        controlState?.default ||
        'undefined'
      ).toString();
      sessionStorage.setItem('time_grain_sqla.value', currentValue);
    }
    return {
      choices: (state.datasource as Dataset)?.time_grain_sqla,
    };
  },
  validators: [timeGranValidForTimeRange],
  visibility: displayTimeRelatedControls,
};

const time_range: SharedControlConfig<'DateFilterControl'> = {
  type: 'DateFilterControl',
  freeForm: true,
  label: TIME_FILTER_LABELS.time_range,
  default: 'Last day', // this value is an empty filter constant so shouldn't translate it.
  description: t(
    'This control filters the whole chart based on the selected time range. All relative times, e.g. "Last month", ' +
      '"Last 7 days", "now", etc. are evaluated on the server using the server\'s ' +
      'local time (sans timezone). All tooltips and placeholder times are expressed ' +
      'in UTC (sans timezone). The timestamps are then evaluated by the database ' +
      "using the engine's local timezone. Note one can explicitly set the timezone " +
      'per the ISO 8601 format if specifying either the start and/or end time.',
  ),
  validators: [timeRangeValidForTimeGran],
  mapStateToProps: state => ({
    datasource: state.datasource,
  }),
};

const time_ui_frame: SharedControlConfig<'HiddenControl'> = {
  type: 'HiddenControl',
  label: TIME_FILTER_LABELS.time_ui_frame,
  description: t(
    'This control time range type. including of Custom,Advanced...',
  ),
  validators: [validateTimeRangeType],
};

const row_limit: SharedControlConfig<'SelectControl'> = {
  type: 'SelectControl',
  freeForm: true,
  label: t('Row limit'),
  clearable: false,
  mapStateToProps: state => ({ maxValue: state?.common?.conf?.SQL_MAX_ROW }),
  validators: [
    legacyValidateInteger,
    (v, state) => validateMaxValue(v, state?.maxValue || DEFAULT_MAX_ROW),
  ],
  default: 10000,
  choices: formatSelectOptions(ROW_LIMIT_OPTIONS),
  description: t(
    'Limits the number of the rows that are computed in the query that is the source of the data used for this chart.',
  ),
};

const order_desc: SharedControlConfig<'CheckboxControl'> = {
  type: 'CheckboxControl',
  label: t('Sort Descending'),
  default: true,
  description: t(
    'If enabled, this control sorts the results/values descending, otherwise it sorts the results ascending.',
  ),
  visibility: ({ controls }) =>
    Boolean(
      controls?.timeseries_limit_metric.value &&
        !isEmpty(controls?.timeseries_limit_metric.value),
    ),
};

const limit: SharedControlConfig<'SelectControl'> = {
  type: 'SelectControl',
  freeForm: true,
  label: t('Series limit'),
  placeholder: t('None'),
  validators: [legacyValidateInteger],
  choices: formatSelectOptions(SERIES_LIMITS),
  clearable: true,
  description: t(
    'Limits the number of series that get displayed. A query to find the top n series by the first measure ' +
      'is applied to limit the number of series that get ' +
      'fetched and rendered. This feature is useful when grouping by high cardinality ' +
      'column(s) though does increase the query complexity and cost.',
  ),
  shouldMapStateToProps() {
    return true;
  },
  mapStateToProps: state => {
    const groupByColumns = state.form_data.groupby;
    if (
      groupByColumns &&
      groupByColumns.length > 0 &&
      state.controls?.limit?.value === undefined
    ) {
      // set defeault value to 5 if group by columns are set
      return { value: 5 };
    }
    return {};
  },
};

const series_limit: SharedControlConfig<'SelectControl'> = {
  type: 'SelectControl',
  freeForm: true,
  label: t('Series limit'),
  placeholder: t('None'),
  validators: [legacyValidateInteger],
  choices: formatSelectOptions(SERIES_LIMITS),
  description: t(
    'Limits the number of series that get displayed. A query to find the top n series by the first measure ' +
      'is applied to limit the number of series that get ' +
      'fetched and rendered. This feature is useful when grouping by high cardinality ' +
      'column(s) though does increase the query complexity and cost.',
  ),
};

const y_axis_format: SharedControlConfig<'SelectControl', SelectDefaultOption> =
  {
    type: 'SelectControl',
    freeForm: true,
    label: t('Y Axis Format'),
    renderTrigger: true,
    default: DEFAULT_NUMBER_FORMAT,
    choices: D3_FORMAT_OPTIONS,
    description: D3_FORMAT_DOCS,
    tokenSeparators: ['\n', '\t', ';'],
    filterOption: ({ data: option }, search) =>
      option.label.includes(search) || option.value.includes(search),
    mapStateToProps: state => {
      const isPercentage =
        state.controls?.comparison_type?.value === ComparisonType.Percentage;
      return {
        choices: isPercentage
          ? D3_FORMAT_OPTIONS.filter(option => option[0].includes('%'))
          : D3_FORMAT_OPTIONS,
      };
    },
  };

const currency_format: SharedControlConfig<'CurrencyControl'> = {
  type: 'CurrencyControl',
  label: t('Currency format'),
  renderTrigger: true,
};

const x_axis_time_format: SharedControlConfig<
  'SelectControl',
  SelectDefaultOption
> = {
  type: 'SelectControl',
  freeForm: true,
  label: t('Time format'),
  renderTrigger: true,
  default: DEFAULT_TIME_FORMAT,
  choices: D3_TIME_FORMAT_OPTIONS,
  description: D3_TIME_FORMAT_DOCS,
  filterOption: ({ data: option }, search) =>
    option.label.includes(search) || option.value.includes(search),
};

const color_scheme: SharedControlConfig<'ColorSchemeControl'> = {
  type: 'ColorSchemeControl',
  label: t('Color Scheme'),
  default: categoricalSchemeRegistry.getDefaultKey(),
  renderTrigger: true,
  choices: () => categoricalSchemeRegistry.keys().map(s => [s, s]),
  description: t('The color scheme for rendering chart'),
  schemes: () => categoricalSchemeRegistry.getMap(),
  mapStateToProps: state => ({
    dashboardId: state?.form_data?.dashboardId,
  }),
};

const truncate_metric: SharedControlConfig<'CheckboxControl'> = {
  type: 'CheckboxControl',
  label: t('Truncate Metric'),
  default: true,
  description: t('Whether to truncate metrics'),
};

const show_empty_columns: SharedControlConfig<'CheckboxControl'> = {
  type: 'CheckboxControl',
  label: t('Show empty columns'),
  default: true,
  description: t('Show empty columns'),
};

const temporal_columns_lookup: SharedControlConfig<'HiddenControl'> = {
  type: 'HiddenControl',
  initialValue: (control: ControlState, state: ControlPanelState | null) =>
    Object.fromEntries(
      ensureIsArray<Record<string, any>>(state?.datasource?.columns)
        .filter(option => option.is_dttm)
        .map(option => [option.column_name ?? option.name, option.is_dttm]),
    ),
};

const sort_by_metric: SharedControlConfig<'CheckboxControl'> = {
  type: 'CheckboxControl',
  label: t('Sort by metric'),
  description: t(
    'Whether to sort results by the selected metric in descending order.',
  ),
};

const sample_rate: SharedControlConfig<'SelectControl'> = {
  type: 'SelectControl',
  freeForm: true,
  label: t('Sample rate'),
  default: 0.1,
  placeholder: t('None'),
  choices: SAMPLE_RATE_OPTIONS,
  description: t('Apply sampling on the queried dataset'),
  shouldMapStateToProps: () => true,
  mapStateToProps: ({ controls }) => sampleRateMapStateToProps(controls),
  visibility: ({ controls }) => isSampleRateVisible(controls),
};

export function getSampleMode(controls: ControlStateMapping): SampleMode {
  const mode = controls?.sample_mode?.value;
  if (mode === SampleMode.Sampled || mode === SampleMode.Unsampled) {
    return mode as SampleMode;
  }
  return SampleMode.Sampled;
}

export function sampleRateMapStateToProps(controls: ControlStateMapping) {
  const isUnsampled = controls?.sample_mode?.value === SampleMode.Unsampled;
  const warning = getSamplingWarning(
    controls,
    isUnsampled ? [] : getMeasuredSamplingUnsupportedColumns(controls),
  );

  return {
    disabled: isUnsampled || warning !== undefined,
    warning,
  };
}

export function getVisibleSampleModeState(
  controls: ControlStateMapping,
  verifiedSamplingAllowed = false,
) {
  const warning = getSamplingWarning(
    controls,
    undefined,
    verifiedSamplingAllowed,
  );

  if (warning !== undefined) {
    return {
      ...(hasMeasurementsOrActor(controls) ? { value: SampleMode.Unset } : {}),
      disabled: true,
      warning,
    };
  }

  return {
    disabled: false,
    value:
      verifiedSamplingAllowed &&
      getMeasuredSamplingUnsupportedColumns(controls).length > 0 &&
      controls.sample_mode?.value !== SampleMode.Sampled
        ? SampleMode.Unsampled
        : getSampleMode(controls),
    warning: undefined,
  };
}

export function sampleModeMapStateToProps(
  controls: ControlStateMapping,
  verifiedSamplingAllowed = false,
) {
  if (!isSampleModeVisible(controls)) {
    return {
      initialValue: SampleMode.Unset,
      disabled: false,
      value: SampleMode.Unset,
      warning: undefined,
    };
  }

  const state = getVisibleSampleModeState(controls, verifiedSamplingAllowed);
  return {
    ...state,
    ...(state.value === SampleMode.Unsampled &&
    controls.sample_mode?.value !== SampleMode.Unsampled
      ? { initialValue: SampleMode.Unsampled }
      : {}),
  };
}

type SamplingQualificationCheck = (
  controls: ControlStateMapping,
) => string | undefined;

const checkRollingWindowSamplingConflict: SamplingQualificationCheck =
  controls =>
    controls?.rolling_type?.value
      ? 'Sampling is not supported when a rolling window is applied.'
      : undefined;

const BACKEND_SAMPLING_QUALIFICATION_CHECKS: Record<
  string,
  SamplingQualificationCheck[]
> = {
  clickhouse: [checkRollingWindowSamplingConflict],
  kustokql: [],
};

export function getSamplingWarning(
  controls: ControlStateMapping,
  samplingUnsupportedColumns = getMeasuredSamplingUnsupportedColumns(controls),
  verifiedSamplingAllowed = false,
): string | undefined {
  if (!hasMeasurementsOrActor(controls)) {
    return 'Sampling is not applicable without measurements or actor column.';
  }

  const backend = controls?.datasource?.datasource?.database?.backend;
  const checks = backend
    ? (BACKEND_SAMPLING_QUALIFICATION_CHECKS[backend] ?? [])
    : [];
  const backendWarning = checks
    .map(check => check(controls))
    .find((warning): warning is string => warning !== undefined);
  if (backendWarning) {
    return backendWarning;
  }

  const availableSamplingColumns = getSamplingSafeColumns(controls);
  if (!availableSamplingColumns.length) {
    return 'Sampling is not supported for this dataset.';
  }
  const useVerifiedMapping =
    backend === 'kustokql' &&
    verifiedSamplingAllowed &&
    samplingUnsupportedColumns.length > 0;
  const unsupportedColumns = useVerifiedMapping
    ? getMeasuredSamplingUnsupportedColumns(controls, true)
    : samplingUnsupportedColumns;
  if (!unsupportedColumns.length) {
    if (
      backend === 'kustokql' &&
      (isFeatureEnabled(FeatureFlag.KustoHybridSampling) || useVerifiedMapping)
    ) {
      return getHybridSamplingWarning(controls, useVerifiedMapping);
    }
    return undefined;
  }

  return `This query will be run in unsampled mode. [${unsupportedColumns.join(
    ', ',
  )}] as the ${
    unsupportedColumns.length === 1 ? 'measurement is' : 'measurements are'
  } not yet supported in sampling mode. Columns currently supported for sampling mode: ${availableSamplingColumns.join(
    ', ',
  )}`;
}

export function getSamplingSafeColumns(
  controls: ControlStateMapping,
): string[] {
  const datasource = controls?.datasource?.datasource;
  if (
    datasource?.database?.backend === 'kustokql' &&
    isFeatureEnabled(FeatureFlag.KustoHybridSampling)
  ) {
    const mappings: Dataset['kusto_sampling_mappings'] =
      datasource.kusto_sampling_mappings;
    return [
      ...new Set(mappings?.flatMap(mapping => mapping.aggregate_columns) ?? []),
    ];
  }
  return (
    controls?.datasource?.datasource?.columns
      ?.filter(
        (column: { system_tags?: { name?: string }[] }) =>
          Array.isArray(column.system_tags) &&
          column.system_tags.some(
            tag =>
              tag &&
              typeof tag.name === 'string' &&
              tag.name === 'SamplingSafeColumn',
          ),
      )
      ?.map((column: { column_name: any }) => column.column_name) ?? []
  );
}

const SAMPLING_SAFE_AGGREGATORS = new Set([
  'COUNT',
  'COUNT_DISTINCT',
  'UNIQUE',
]);

type SamplingMeasurement = {
  SelectControlColumn?: string;
  SelectControlAggregator?: string;
  MeasureName?: string;
  isCalculatedMeasure?: boolean;
  isValidExpression?: boolean;
  metric?: QueryFormMetric;
  AdhocFilterControl?: { savedCohorts?: number[] }[];
  calculatedMeasures?: {
    type?: string;
    aggregator?: string;
    column?: { column_name?: string };
  }[];
};

function getCalculatedSamplingAggregations(
  measurement: SamplingMeasurement,
): SamplingMeasurement[] | undefined {
  const parts = measurement.calculatedMeasures;
  const references = Array.isArray(parts)
    ? parts.filter(
        part => part?.type === 'ChartMeasure' || part?.type === 'SavedMeasure',
      )
    : [];
  if (
    measurement.isValidExpression === false ||
    !references.length ||
    references.some(
      reference => !reference.column?.column_name || !reference.aggregator,
    ) ||
    parts?.some(
      part =>
        !['ChartMeasure', 'SavedMeasure', 'Number', 'Operator'].includes(
          part?.type ?? '',
        ),
    )
  ) {
    return undefined;
  }
  return references.map(reference => ({
    SelectControlColumn: reference.column?.column_name,
    SelectControlAggregator: reference.aggregator,
  }));
}

function getHybridSamplingWarning(
  controls: ControlStateMapping,
  useVerifiedMapping = false,
): string | undefined {
  const measurements = (controls.measurements?.value ??
    []) as SamplingMeasurement[];
  const aggregations = measurements.flatMap(measurement =>
    measurement.isCalculatedMeasure
      ? (getCalculatedSamplingAggregations(measurement) ?? [])
      : [measurement],
  );
  const groups = ['groupby', 'groupbyRows', 'groupbyColumns'].flatMap(
    field => (controls[field]?.value ?? []) as unknown[],
  );
  const ranking =
    controls.series_limit_metric?.value ||
    controls.timeseries_limit_metric?.value ||
    controls.legacy_order_by?.value;
  if (
    ranking &&
    !measurements.some(measurement => isEqual(measurement.metric, ranking))
  ) {
    if (
      typeof ranking !== 'object' ||
      Array.isArray(ranking) ||
      ranking.expressionType !== 'SIMPLE'
    ) {
      return 'This query will use full data because its ranking measure has no supported sampling key.';
    }
    const { column } = ranking;
    if (
      !column ||
      typeof column !== 'object' ||
      Array.isArray(column) ||
      typeof column.column_name !== 'string' ||
      typeof ranking.aggregate !== 'string' ||
      !SAMPLING_SAFE_AGGREGATORS.has(ranking.aggregate)
    ) {
      return 'This query will use full data because its ranking measure has no supported sampling key.';
    }
    aggregations.push({
      SelectControlColumn: column.column_name,
      SelectControlAggregator: ranking.aggregate,
    });
  }
  const keys = new Set(aggregations.map(item => item.SelectControlColumn));
  const actor = controls.actor_col?.value as string | undefined;
  if (actor) {
    keys.add(actor);
  }
  const configuredMappings: Dataset['kusto_sampling_mappings'] =
    controls.datasource?.datasource?.kusto_sampling_mappings;
  const verifiedMapping = useVerifiedMapping
    ? configuredMappings?.find(
        mapping =>
          mapping.sampled_table_name === 'onebi_ww_sampled_1pct' &&
          mapping.aggregate_columns.length === 1,
      )
    : undefined;
  const key = verifiedMapping?.aggregate_columns[0] ?? [...keys][0];
  const mappings = verifiedMapping
    ? [verifiedMapping]
    : (configuredMappings?.filter(
        mapping => key && mapping.aggregate_columns.includes(key),
      ) ?? []);
  if ((!verifiedMapping && keys.size !== 1) || mappings.length !== 1) {
    return 'This query will use full data because all measures must share one aggregate key and sampled table.';
  }
  const otherKeys = mappings[0].aggregate_columns.filter(
    column => column !== key,
  );
  const computedColumns = new Set(
    controls.datasource?.datasource?.columns
      ?.filter((column: { expression?: string }) => column.expression)
      .map((column: { column_name: string }) => column.column_name),
  );
  if (
    otherKeys.length &&
    groups.some(
      group =>
        typeof group !== 'string' ||
        otherKeys.includes(group) ||
        computedColumns.has(group),
    )
  ) {
    return 'This query will use full data because its dimensions require a different or unresolved entity key.';
  }
  const filters = [
    ...((controls.adhoc_filters?.value ?? []) as { savedCohorts?: number[] }[]),
    ...measurements.flatMap(
      measurement => measurement.AdhocFilterControl ?? [],
    ),
  ];
  const cohortIds = new Set(
    filters.flatMap(filter => filter.savedCohorts ?? []),
  );
  const cohorts: {
    id: number;
    form_data?: { actor?: string; savedCohorts?: number[] };
  }[] = controls.datasource?.datasource?.saved_cohorts ?? [];
  for (const id of cohortIds) {
    const cohort = cohorts.find(item => item.id === id);
    if (cohort?.form_data?.actor !== key) {
      return 'This query will use full data because its cohort requires a different or unresolved entity key.';
    }
    cohort?.form_data?.savedCohorts?.forEach(cohortId =>
      cohortIds.add(cohortId),
    );
  }
  return undefined;
}

export function getMeasuredSamplingUnsupportedColumns(
  controls: ControlStateMapping,
  allowUnsupportedColumns = false,
): string[] {
  const availableSamplingColumns = getSamplingSafeColumns(controls);
  const isKusto =
    controls?.datasource?.datasource?.database?.backend === 'kustokql';
  const notAllowedColumns: string[] = [];
  if (controls?.measurements?.value) {
    (controls.measurements.value as SamplingMeasurement[]).forEach(
      measurement => {
        const aggregations =
          isKusto && measurement?.isCalculatedMeasure
            ? getCalculatedSamplingAggregations(measurement)
            : [measurement];
        if (!aggregations) {
          notAllowedColumns.push(
            measurement.MeasureName || t('Calculated measure'),
          );
          return;
        }
        aggregations.forEach(aggregation => {
          const column = aggregation?.SelectControlColumn as string;
          const aggregator = aggregation?.SelectControlAggregator as string;
          if (
            !column ||
            (!allowUnsupportedColumns &&
              !availableSamplingColumns.includes(column))
          ) {
            notAllowedColumns.push(column);
          } else if (!SAMPLING_SAFE_AGGREGATORS.has(aggregator)) {
            notAllowedColumns.push(`${aggregator}(${column})`);
          }
        });
      },
    );
  }

  // For the user funnel and flexible funnel charts which use the actor item instead of measurements
  const actor: string = (controls?.actor_col?.value as string) || '';
  if (actor && !availableSamplingColumns.includes(actor)) {
    notAllowedColumns.push(actor);
  }

  return notAllowedColumns;
}

export const sample_mode: SharedControlConfig<'RadioButtonControl'> = {
  type: 'RadioButtonControl',
  label: t('Sampling mode'),
  default: SampleMode.Sampled,
  options: [
    [SampleMode.Sampled, SampleModeLabel[SampleMode.Sampled]],
    [SampleMode.Unsampled, SampleModeLabel[SampleMode.Unsampled]],
  ],
  shouldMapStateToProps: () => true,
  mapStateToProps: ({ controls, slice, datasource }) =>
    sampleModeMapStateToProps(
      controls,
      slice?.sampling_qualification?.verified_sampling_allowed === true &&
        slice.form_data?.datasource ===
          `${datasource?.id}__${datasource?.type}`,
    ),
  visibility: ({ controls }) => isSampleModeVisible(controls),
};

const hasMeasurementsOrActor = (controls: ControlStateMapping) => {
  const hasMeasurements =
    controls?.measurements?.value &&
    (controls.measurements.value as JsonArray).length > 0;
  const hasActor = !!(controls?.actor_col?.value as string);

  return hasMeasurements || hasActor;
};

export default {
  metrics: dndAdhocMetricsControl,
  metric: dndAdhocMetricControl,
  datasource: datasourceControl,
  viz_type,
  color_picker,
  metric_2: dndAdhocMetricControl2,
  linear_color_scheme,
  secondary_metric: dndSecondaryMetricControl,
  groupby: dndGroupByControl,
  columns: dndColumnsControl,
  granularity,
  granularity_sqla: dndGranularitySqlaControl,
  time_grain_sqla,
  time_range,
  time_ui_frame,
  row_limit,
  limit,
  timeseries_limit_metric: dndSortByControl,
  orderby: dndSortByControl,
  order_desc,
  series: dndSeriesControl,
  entity: dndEntityControl,
  x: dndXControl,
  y: dndYControl,
  size: dndSizeControl,
  y_axis_format,
  x_axis_time_format,
  adhoc_filters: dndAdhocFilterControl,
  color_scheme,
  series_columns: dndColumnsControl,
  series_limit,
  series_limit_metric: dndSortByControl,
  legacy_order_by: dndSortByControl,
  truncate_metric,
  x_axis: dndXAxisControl,
  show_empty_columns,
  temporal_columns_lookup,
  currency_format,
  sort_by_metric,
  measurements,
  measurement_metrics: dndAdhocMetricsControl,
  sample_rate,
  sample_mode,
};
